/** Human labels for JournalSourceType — never show raw enum in primary UI. */

import { journalStatusLabel } from '../ui/statusCopy';

export type SourceBadge = {
  label: string;
  tone: string;
};

const MAP: Record<string, SourceBadge> = {
  MANUAL: { label: 'MANUAL', tone: 'bg-slate-100 text-slate-800' },
  LOAN: { label: 'PRÉSTAMO', tone: 'bg-indigo-50 text-indigo-800' },
  LOAN_DISBURSEMENT: { label: 'PRÉSTAMO', tone: 'bg-indigo-50 text-indigo-800' },
  LOAN_PAYOFF: { label: 'PRÉSTAMO', tone: 'bg-indigo-50 text-indigo-800' },
  LOAN_REFINANCING: { label: 'PRÉSTAMO', tone: 'bg-indigo-50 text-indigo-800' },
  PAYMENT: { label: 'COBRANZA', tone: 'bg-emerald-50 text-emerald-800' },
  PAYMENT_REVERSAL: { label: 'COBRANZA', tone: 'bg-emerald-50 text-emerald-800' },
  CASH_MOVEMENT: { label: 'CAJA', tone: 'bg-amber-50 text-amber-900' },
  CASH_TRANSFER: { label: 'CAJA', tone: 'bg-amber-50 text-amber-900' },
  BANK_MOVEMENT: { label: 'BANCO', tone: 'bg-sky-50 text-sky-900' },
  BANK_FEE: { label: 'BANCO', tone: 'bg-sky-50 text-sky-900' },
  BANK_INTEREST: { label: 'BANCO', tone: 'bg-sky-50 text-sky-900' },
  BANK_RECON_ADJUST: { label: 'BANCO', tone: 'bg-sky-50 text-sky-900' },
  SUPPLIER_INVOICE: { label: 'PROVEEDOR', tone: 'bg-orange-50 text-orange-900' },
  SUPPLIER_PAYMENT: { label: 'PROVEEDOR', tone: 'bg-orange-50 text-orange-900' },
  EXPENSE: { label: 'PROVEEDOR', tone: 'bg-orange-50 text-orange-900' },
  INFLATION_ADJUSTMENT: { label: 'INFLACIÓN', tone: 'bg-rose-50 text-rose-900' },
  FX_REVALUATION: { label: 'FX', tone: 'bg-violet-50 text-violet-900' },
  JOURNAL_IMPORT: { label: 'IMPORTACIÓN', tone: 'bg-teal-50 text-teal-900' },
  OPENING_BALANCE_IMPORT: { label: 'IMPORTACIÓN', tone: 'bg-teal-50 text-teal-900' },
  OPENING: { label: 'APERTURA', tone: 'bg-gray-100 text-gray-800' },
  YEAR_OPENING: { label: 'APERTURA', tone: 'bg-gray-100 text-gray-800' },
  RESULT_CLOSING: { label: 'CIERRE', tone: 'bg-gray-100 text-gray-800' },
  REVERSAL: { label: 'REVERSIÓN', tone: 'bg-red-50 text-red-800' },
  WORKING_PAPER: { label: 'PAPELES', tone: 'bg-stone-100 text-stone-800' },
  ADJUSTMENT: { label: 'AJUSTE', tone: 'bg-slate-100 text-slate-800' },
  WAIVER: { label: 'QUITA', tone: 'bg-fuchsia-50 text-fuchsia-900' },
  PAYROLL_CLEARING: { label: 'HABERES', tone: 'bg-cyan-50 text-cyan-900' },
  PAYROLL_SETTLEMENT: { label: 'HABERES', tone: 'bg-cyan-50 text-cyan-900' },
  CUSTOMER_CREDIT: { label: 'CRÉDITO', tone: 'bg-lime-50 text-lime-900' },
  LEGACY_MIGRATION: { label: 'MIGRACIÓN', tone: 'bg-gray-100 text-gray-700' },
};

/** Quick-filter options shown in UI (collapsed groups). */
export const SOURCE_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los orígenes' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'LOAN_DISBURSEMENT', label: 'Préstamo' },
  { value: 'PAYMENT', label: 'Cobranza' },
  { value: 'CASH_MOVEMENT', label: 'Caja' },
  { value: 'BANK_MOVEMENT', label: 'Banco' },
  { value: 'SUPPLIER_INVOICE', label: 'Proveedor' },
  { value: 'INFLATION_ADJUSTMENT', label: 'Inflación' },
  { value: 'FX_REVALUATION', label: 'FX' },
  { value: 'JOURNAL_IMPORT', label: 'Importación' },
];

export function sourceBadge(sourceType?: string | null): SourceBadge {
  if (!sourceType) return { label: '—', tone: 'bg-gray-50 text-gray-600' };
  return MAP[sourceType] ?? { label: sourceType.replace(/_/g, ' '), tone: 'bg-gray-100 text-gray-700' };
}

export function statusLabel(status?: string | null): string {
  return journalStatusLabel(status);
}

export type OriginLink = { label: string; to: string };

export function originLinks(entry: {
  sourceType?: string | null;
  sourceId?: string | null;
}): OriginLink[] {
  const t = entry.sourceType ?? '';
  const id = entry.sourceId;
  if (t === 'LOAN' || t === 'LOAN_DISBURSEMENT' || t === 'LOAN_PAYOFF' || t === 'LOAN_REFINANCING') {
    return [{ label: 'Ver préstamo', to: id ? `/loans/${id}` : '/loans' }];
  }
  if (t === 'PAYMENT' || t === 'PAYMENT_REVERSAL') {
    return [{ label: 'Ver cobranza', to: id ? `/payments?paymentId=${id}` : '/payments' }];
  }
  if (t === 'CASH_MOVEMENT' || t === 'CASH_TRANSFER') {
    return [{ label: 'Ver movimiento caja', to: '/cash' }];
  }
  if (t.startsWith('BANK_')) {
    return [{ label: 'Ver bancos', to: '/bank-reconciliation' }];
  }
  if (t === 'SUPPLIER_INVOICE' || t === 'SUPPLIER_PAYMENT' || t === 'EXPENSE') {
    return [{ label: 'Ver proveedores', to: '/payables' }];
  }
  if (t === 'INFLATION_ADJUSTMENT') {
    return [{ label: 'Ver inflación', to: '/accounting/tools/inflation' }];
  }
  if (t === 'FX_REVALUATION') {
    return [{ label: 'Ver multimoneda', to: '/accounting/tools/fx' }];
  }
  if (t === 'JOURNAL_IMPORT' || t === 'OPENING_BALANCE_IMPORT') {
    return [{ label: 'Ver importaciones', to: '/accounting/tools/imports' }];
  }
  return [];
}

export function formatMoney(v: number | string | null | undefined): string {
  return Number(v ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
